import { Button, Card, Chip, faDigits, Field, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type SiteRow, type StockBalanceRow, type StockItemRow, type WarehouseRow } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";
import { formatMilli, formatQty, formatRial } from "./format.js";

export function WarehousesTab() {
  const warehouses = useQuery({
    queryKey: ["inventory", "warehouses"],
    queryFn: () => api<WarehouseRow[]>("/inventory/warehouses"),
  });
  const items = useQuery({
    queryKey: ["inventory", "items"],
    queryFn: () => api<StockItemRow[]>("/inventory/items"),
  });
  const sites = useQuery({
    queryKey: ["sites"],
    queryFn: () => api<SiteRow[]>("/sites"),
  });

  const [selectedKitchenId, setSelectedKitchenId] = useState<string>("all");
  const [selectedWhId, setSelectedWhId] = useState<string>("");
  const [showAdd, setShowAdd] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newKitchenId, setNewKitchenId] = useState<string>("");

  const kitchens = (sites.data ?? []).filter((s) => s.kind === "kitchen");
  const kitchensMap = new Map(kitchens.map((k) => [k.id, k.name]));

  const allActiveWhs = (warehouses.data ?? []).filter((w) => w.status === "active");
  const activeWhs =
    selectedKitchenId === "all"
      ? allActiveWhs
      : allActiveWhs.filter((w) =>
          selectedKitchenId === "none" ? !w.kitchenId : w.kitchenId === selectedKitchenId,
        );

  const currentWhId =
    selectedWhId && activeWhs.some((w) => w.id === selectedWhId) ? selectedWhId : (activeWhs[0]?.id ?? "");

  const balances = useQuery({
    queryKey: ["inventory", "warehouses", currentWhId, "balance"],
    queryFn: () => api<StockBalanceRow[]>(`/inventory/warehouses/${currentWhId}/balance`),
    enabled: Boolean(currentWhId),
  });

  const addAction = useAction(async () => {
    await api("/inventory/warehouses", {
      body: {
        code: newCode.trim(),
        name: newName.trim(),
        kitchenId: newKitchenId || null,
        isVirtual: false,
        status: "active",
      },
    });
    setNewCode("");
    setNewName("");
    setNewKitchenId("");
    setShowAdd(false);
  }, [["inventory", "warehouses"]]);

  const archiveAction = useAction(
    async (wh: WarehouseRow) => {
      const reason = window.prompt(`دلیل غیرفعال‌سازی انبار «${wh.name}»؟`);
      if (!reason?.trim()) return;
      await api(`/inventory/warehouses/${wh.id}/archive`, {
        body: { version: wh.version, reason: reason.trim() },
      });
    },
    [["inventory", "warehouses"]],
  );

  const itemsMap = new Map((items.data ?? []).map((i) => [i.id, i]));
  const currentWh = allActiveWhs.find((w) => w.id === currentWhId);

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <div className="row wrap justify-between items-center" style={{ gap: "var(--s3)" }}>
        <div className="row wrap items-center" style={{ gap: "var(--s2)" }}>
          <label className="hint" htmlFor="kitchen-filter">
            فیلتر آشپزخانه:
          </label>
          <select
            id="kitchen-filter"
            className="input"
            value={selectedKitchenId}
            onChange={(e) => {
              setSelectedKitchenId(e.target.value);
            }}
            style={{ width: "auto" }}
          >
            <option value="all">همه آشپزخانه‌ها</option>
            {kitchens.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
            <option value="none">بدون آشپزخانه (مرکزی)</option>
          </select>
        </div>
        <Button
          variant="default"
          icon="plus"
          onClick={() => {
            setShowAdd(!showAdd);
          }}
        >
          افزودن انبار جدید
        </Button>
      </div>

      {showAdd && (
        <Card>
          <div className="stack" style={{ gap: "var(--s3)" }}>
            <h3 className="t-card">تعریف انبار تازه</h3>
            <div className="row wrap" style={{ gap: "var(--s3)" }}>
              <Field
                id="wh-code"
                label="کد انبار"
                value={newCode}
                onChange={(e) => {
                  setNewCode(e.target.value);
                }}
                placeholder="مثلاً WH-EAST"
              />
              <Field
                id="wh-name"
                label="نام انبار"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                }}
                placeholder="مثلاً انبار شرق"
              />
              <div className="field">
                <label htmlFor="wh-kitchen">آشپزخانه مرتبط</label>
                <select
                  id="wh-kitchen"
                  className="input"
                  value={newKitchenId}
                  onChange={(e) => {
                    setNewKitchenId(e.target.value);
                  }}
                >
                  <option value="">بدون آشپزخانه (انبار مرکزی / مستقل)</option>
                  {kitchens.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <ErrorLine error={addAction.error} />
            <div className="row" style={{ gap: "var(--s2)" }}>
              <Button
                variant="primary"
                disabled={!newCode.trim() || !newName.trim() || addAction.busy}
                onClick={() => {
                  addAction.run(undefined);
                }}
              >
                ثبت انبار
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowAdd(false);
                }}
              >
                انصراف
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="row wrap" style={{ gap: "var(--s2)" }}>
        {activeWhs.length === 0 ? (
          <p className="hint">هیچ انباری با این فیلتر یافت نشد.</p>
        ) : (
          activeWhs.map((wh) => (
            <Button
              key={wh.id}
              variant={wh.id === currentWhId ? "primary" : "ghost"}
              onClick={() => {
                setSelectedWhId(wh.id);
              }}
            >
              {wh.name} ({wh.code})
              {wh.kitchenId && kitchensMap.has(wh.kitchenId)
                ? ` [${kitchensMap.get(wh.kitchenId) ?? ""}]`
                : ""}
            </Button>
          ))
        )}
      </div>

      {currentWh && (
        <Panel
          title={`موجودی انبار: ${currentWh.name} (${currentWh.code})`}
          actions={
            <Button
              variant="urgent"
              size="md"
              onClick={() => {
                archiveAction.run(currentWh);
              }}
            >
              غیرفعال‌سازی این انبار
            </Button>
          }
        >
          <ErrorLine error={archiveAction.error} />
          {balances.isLoading ? (
            <p className="hint">در حال دریافت موجودی...</p>
          ) : (balances.data ?? []).length === 0 ? (
            <p className="hint">موجودی این انبار صفر است.</p>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>کد کالا</th>
                    <th>نام کالا</th>
                    <th>موجودی</th>
                    <th>واحد</th>
                    <th>بهای میانگین (تومان)</th>
                    <th>ارزش کل (تومان)</th>
                    <th>وضعیت</th>
                  </tr>
                </thead>
                <tbody>
                  {(balances.data ?? []).map((b) => {
                    const item = itemsMap.get(b.itemId);
                    const minStockMilli = item?.minStockMilli ?? 0;
                    const isLow = minStockMilli > 0 && b.quantityMilli < minStockMilli;

                    return (
                      <tr key={b.itemId}>
                        <td>{item?.code ?? "—"}</td>
                        <td>{item?.name ?? b.itemId}</td>
                        <td className="num">{formatQty(b.quantityMilli)}</td>
                        <td>{item?.baseUnit ?? "—"}</td>
                        <td className="num">{formatRial(b.averageUnitCostRial)}</td>
                        <td className="num">{formatRial(b.totalValueRial)}</td>
                        <td>
                          {isLow ? (
                            <Chip tone="coral">کسری از حداقل ({faDigits(formatMilli(minStockMilli))})</Chip>
                          ) : (
                            <Chip tone="ok">کافی</Chip>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
